// Seller Settings → ShipStation strings — EN + TR.
//
// Etsy hides buyer addresses from PEXIFY; the seller's own ShipStation account
// has them. The seller saves their ShipStation API key + secret here and PEXIFY
// fills their Etsy order addresses from it (D-14 LIGHT 2026-09-15).
//
// Source: features/settings/ShipStationCredential.tsx.

import type { LocalizedDict } from '../index'

type ShipStationCredentialStrings = {
  pageTitle: string
  pageDescription: string
  simulation: string
  loading: string
  loadFailed: string
  readOnly: string
  connected: string
  notConnected: string
  keyHint: (hint: string) => string
  etsyStores: (n: number) => string
  verifiedAt: (when: string) => string
  lastFill: (when: string, n: number) => string
  lastError: (message: string) => string
  howTo: {
    title: string
    steps: string[]
  }
  fields: {
    apiKey: string
    apiSecret: string
    secretNote: string
  }
  actions: {
    save: string
    rotate: string
    saving: string
    change: string
    cancel: string
    remove: string
    removeConfirm: string
  }
  toast: {
    saved: string
    savedDescription: (n: number) => string
    saveFailed: string
    removed: string
    removeFailed: string
  }
}

export const shipStationCredentialI18n: LocalizedDict<ShipStationCredentialStrings> = {
  en: {
    pageTitle: 'ShipStation',
    pageDescription:
      'Etsy does not share buyer addresses with PEXIFY. Connect your own ShipStation account and PEXIFY fills the ship-to address of your Etsy orders from it automatically.',
    simulation: 'Simulation mode — saving a ShipStation key requires the live backend.',
    loading: 'Loading…',
    loadFailed: 'Could not load your ShipStation connection.',
    readOnly: 'Only the account owner can change the ShipStation connection.',
    connected: 'Connected',
    notConnected: 'Not connected',
    keyHint: (hint) => `API key ending in ${hint}`,
    etsyStores: (n) => (n === 1 ? '1 Etsy store found' : `${n} Etsy stores found`),
    verifiedAt: (when) => `Verified ${when}`,
    lastFill: (when, n) =>
      n === 1 ? `Last run ${when} · 1 address filled` : `Last run ${when} · ${n} addresses filled`,
    lastError: (message) => `Last problem: ${message}`,
    howTo: {
      title: 'Where do I find my key?',
      steps: [
        'Sign in to ShipStation.',
        'Open Settings → Account → API Settings.',
        'Copy the API Key and API Secret and paste them below.',
        'Make sure your Etsy stores are connected in that ShipStation account.',
      ],
    },
    fields: {
      apiKey: 'API Key',
      apiSecret: 'API Secret',
      secretNote: 'Checked with ShipStation before saving. Stored encrypted and never shown again.',
    },
    actions: {
      save: 'Verify and save',
      rotate: 'Verify and replace',
      saving: 'Checking with ShipStation…',
      change: 'Change key',
      cancel: 'Cancel',
      remove: 'Disconnect',
      removeConfirm: 'Disconnect ShipStation? Addresses of new Etsy orders will stop filling.',
    },
    toast: {
      saved: 'ShipStation connected',
      savedDescription: (n) =>
        n === 1
          ? '1 Etsy store found. Addresses fill within about 15 minutes.'
          : `${n} Etsy stores found. Addresses fill within about 15 minutes.`,
      saveFailed: 'Could not connect ShipStation',
      removed: 'ShipStation disconnected',
      removeFailed: 'Could not disconnect ShipStation',
    },
  },
  tr: {
    pageTitle: 'ShipStation',
    pageDescription:
      'Etsy alıcı adreslerini PEXIFY ile paylaşmıyor. Kendi ShipStation hesabını bağla; PEXIFY Etsy siparişlerinin teslimat adresini oradan otomatik doldursun.',
    simulation: 'Simülasyon modu — ShipStation anahtarı kaydetmek canlı sistem gerektirir.',
    loading: 'Yükleniyor…',
    loadFailed: 'ShipStation bağlantın yüklenemedi.',
    readOnly: 'ShipStation bağlantısını yalnızca hesap sahibi değiştirebilir.',
    connected: 'Bağlı',
    notConnected: 'Bağlı değil',
    keyHint: (hint) => `Sonu ${hint} ile biten API anahtarı`,
    etsyStores: (n) => `${n} Etsy mağazası bulundu`,
    verifiedAt: (when) => `Doğrulandı: ${when}`,
    lastFill: (when, n) => `Son çalışma ${when} · ${n} adres dolduruldu`,
    lastError: (message) => `Son sorun: ${message}`,
    howTo: {
      title: 'Anahtarımı nerede bulurum?',
      steps: [
        "ShipStation'a giriş yap.",
        'Settings → Account → API Settings sayfasını aç.',
        'API Key ve API Secret değerlerini kopyalayıp aşağıya yapıştır.',
        'Etsy mağazalarının o ShipStation hesabına bağlı olduğundan emin ol.',
      ],
    },
    fields: {
      apiKey: 'API Key',
      apiSecret: 'API Secret',
      secretNote: "Kaydetmeden önce ShipStation'da denenir. Şifreli saklanır, bir daha gösterilmez.",
    },
    actions: {
      save: 'Doğrula ve kaydet',
      rotate: 'Doğrula ve değiştir',
      saving: "ShipStation'da deneniyor…",
      change: 'Anahtarı değiştir',
      cancel: 'Vazgeç',
      remove: 'Bağlantıyı kaldır',
      removeConfirm: 'ShipStation bağlantısı kaldırılsın mı? Yeni Etsy siparişlerinin adresleri dolmaz.',
    },
    toast: {
      saved: 'ShipStation bağlandı',
      savedDescription: (n) => `${n} Etsy mağazası bulundu. Adresler yaklaşık 15 dakika içinde dolar.`,
      saveFailed: 'ShipStation bağlanamadı',
      removed: 'ShipStation bağlantısı kaldırıldı',
      removeFailed: 'ShipStation bağlantısı kaldırılamadı',
    },
  },
}
